import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { useTranslation } from '@gem/modules/i18n';
import { ICommonPopupsProps } from '@gem/popups';

import Tasks from 'APP/Tasks';
import { ISecretChatAccessRequest } from 'APP/model/e2ee/e2eeModel.types';
import Entities from 'STORE';
import { SecretGroup } from 'STORE/Groups/SecretGroup/SecretGroup';

import { ISecretChatRequestAccessPopupProps } from './SecretChatRequestAccessPopup.types';

interface ISecretChatRequestAccessPopupPresenter {
  onClose(): void;
  onBlockAll(): Promise<void>;
  onApproveAll(): Promise<void>;
  onBlock(deviceId: string, userId: string, withoutToasts?: boolean): Promise<void>;
  onApprove(deviceId: string, userId: string, withoutToasts?: boolean): Promise<void>;
  group: SecretGroup | null;
  isPermissionsRequestsEmpty: boolean;
  permissionsRequests: readonly ISecretChatAccessRequest[];
  permissionsRequestsCount: number;
  loadingRequests: Map<string, boolean>;
}

export const useSecretChatRequestAccessPopupPresenter = (
  props: ICommonPopupsProps<ISecretChatRequestAccessPopupProps>
): ISecretChatRequestAccessPopupPresenter => {
  const { t } = useTranslation();

  const presenter = useLocalObservable<ISecretChatRequestAccessPopupPresenter>(() => ({
    loadingRequests: new Map<string, boolean>(),

    onClose(): void {
      props.popupInstance.close();
      props.onClose?.();
    },

    get group(): SecretGroup | null {
      const group = Entities.GroupsStore.getGroupById(props.params!.groupId);

      return !!group && group instanceof SecretGroup ? group : null;
    },

    get permissionsRequests(): readonly ISecretChatAccessRequest[] {
      if (!presenter.group) {
        return [];
      }

      return presenter.group.devicesRequestsAccessList;
    },

    get permissionsRequestsCount(): number {
      return presenter.group?.chatAccessRequestsCount || 0;
    },

    get isPermissionsRequestsEmpty(): boolean {
      return !presenter.permissionsRequests.length;
    },

    async onApproveAll(): Promise<void> {
      const confirmation = await Tasks.app.showConfirm({
        text: t('secure_chat_approve_devices_confirm_description'),
        primaryText: t('secure_chat_approve_all'),
      });

      if (!confirmation) {
        return;
      }

      for await (const request of presenter.permissionsRequests) {
        await presenter.onApprove(request.deviceId, request.userId, true);
      }
      Tasks.app.showToast(t('trusted_device_access_confirmed'));
    },

    async onBlockAll(): Promise<void> {
      try {
        const confirmation = await Tasks.app.showConfirm({
          text: t('secure_chat_block_devices_confirm_description'),
          primaryText: t('secure_chat_block_all'),
        });

        if (!confirmation) {
          return;
        }

        const promises = presenter.permissionsRequests.map((request) =>
          presenter.onBlock(request.deviceId, request.userId, true)
        );
        await Promise.all(promises);
        Tasks.app.showToast(t('secure_chat_access_blocked'));
      } catch (e) {
        Tasks.app.showToast(t('something_went_wrong'));
      }
    },

    async onBlock(deviceId: string, userId: string, withoutToasts?: boolean): Promise<void> {
      if (!withoutToasts) {
        const confirmation = await Tasks.app.showConfirm({
          text: t('secure_chat_block_device_confirm_description'),
          primaryText: t('block'),
        });

        if (!confirmation) {
          return;
        }
      }

      presenter.loadingRequests.set(deviceId, true);

      const response = await Tasks.e2ee.blockSecretGroupRequestAccess({
        deviceId,
        userId,
        groupId: props.params!.groupId,
      });

      if (!withoutToasts && response.result) {
        Tasks.app.showToast(t('secure_chat_access_blocked'));
      }

      if (!withoutToasts && !response.result) {
        Tasks.app.showToast(t('something_went_wrong'));
      }

      presenter.loadingRequests.set(deviceId, false);
    },

    async onApprove(deviceId: string, userId: string, withoutToasts?: boolean): Promise<void> {
      presenter.loadingRequests.set(deviceId, true);

      const response = await Tasks.e2ee.approveSecretGroupRequestAccess({
        deviceId,
        userId,
        groupId: props.params!.groupId,
      });

      if (response.success && !withoutToasts) {
        Tasks.app.showToast(t('trusted_device_access_confirmed'));
      }

      if (!response.success && !withoutToasts && response.showError) {
        Tasks.app.showToast(t('something_went_wrong'));
      }

      presenter.loadingRequests.set(deviceId, false);
    },
  }));

  useEffect(() => {
    if (presenter.isPermissionsRequestsEmpty) {
      presenter.onClose();
    }
  }, [presenter.isPermissionsRequestsEmpty]);

  return presenter;
};
